import React, { useMemo, useEffect } from 'react';
import { motion, useTransform, useMotionValue, useAnimationControls } from 'framer-motion';
import { usePerformance } from '../hooks/usePerformance';

// Wrapper component for scroll-responsive animations
const ScrollResponsiveOrb = ({ orb, speedMultiplier, isMobile, blurAmount, className, background, scaleRange, opacityRange }) => {
    const controls = useAnimationControls();
    
    useEffect(() => {
        const updateAnimation = () => {
            const speed = speedMultiplier.get ? speedMultiplier.get() : 1;
            controls.start({
                x: ['0%', `${orb.x1}%`, `${orb.x2}%`, '0%'],
                y: ['0%', `${orb.y1}%`, `${orb.y2}%`, '0%'],
                scale: scaleRange || (isMobile ? [1, 1.1, 0.95, 1] : [1, 1.2, 0.9, 1]),
                opacity: opacityRange || [
                    orb.opacity,
                    orb.opacity * (isMobile ? 1.3 : 1.5),
                    orb.opacity * (isMobile ? 0.8 : 0.7),
                    orb.opacity,
                ],
                transition: {
                    duration: orb.floatDuration / speed,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: orb.delay,
                    type: "tween",
                }
            });
        };
        
        updateAnimation();
        
        if (speedMultiplier && speedMultiplier.on) {
            const unsubscribe = speedMultiplier.on('change', updateAnimation);
            return unsubscribe;
        }
    }, [controls, orb, speedMultiplier, isMobile, scaleRange, opacityRange]);
    
    return (
        <motion.div
            className={className}
            style={{
                width: `${orb.size}px`,
                height: `${orb.size}px`,
                left: `${orb.x}%`,
                top: `${orb.y}%`,
                background,
                filter: `blur(${blurAmount})`,
                opacity: orb.opacity,
                transform: 'translate3d(-50%, -50%, 0)',
                willChange: 'transform, opacity',
                contain: 'layout style paint',
            }}
            animate={controls}
        />
    );
};

// Wrapper for particles
const ScrollResponsiveParticle = ({ particle, speedMultiplier, className, background }) => {
    const controls = useAnimationControls();
    
    useEffect(() => {
        const updateAnimation = () => {
            const speed = speedMultiplier.get ? speedMultiplier.get() : 1;
            controls.start({
                x: [
                    '0%',
                    `${Math.cos(particle.direction * Math.PI / 180) * particle.distance}%`,
                    `${Math.cos((particle.direction + 90) * Math.PI / 180) * particle.distance}%`,
                    '0%',
                ],
                y: [
                    '0%',
                    `${Math.sin(particle.direction * Math.PI / 180) * particle.distance}%`,
                    `${Math.sin((particle.direction + 90) * Math.PI / 180) * particle.distance}%`,
                    '0%',
                ],
                opacity: [particle.opacity, particle.opacity * 0.3, particle.opacity * 0.6, particle.opacity],
                transition: {
                    duration: particle.duration / speed,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: particle.delay,
                    type: "tween",
                }
            });
        };
        
        updateAnimation();
        
        if (speedMultiplier && speedMultiplier.on) {
            const unsubscribe = speedMultiplier.on('change', updateAnimation);
            return unsubscribe;
        }
    }, [controls, particle, speedMultiplier]);
    
    return (
        <motion.div
            className={className}
            style={{
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                background,
                transform: 'translate3d(-50%, -50%, 0)',
                willChange: 'transform, opacity',
                contain: 'layout style paint',
            }}
            animate={controls}
        />
    );
};

// Wrapper for shapes
const ScrollResponsiveShape = ({ shape, speedMultiplier, className, background, borderRadius }) => {
    const controls = useAnimationControls();
    
    useEffect(() => {
        const updateAnimation = () => {
            const speed = speedMultiplier.get ? speedMultiplier.get() : 1;
            controls.start({
                rotate: [shape.rotation, shape.rotation + 360, shape.rotation + 720, shape.rotation],
                x: ['0%', '15%', '-10%', '0%'],
                y: ['0%', '-15%', '10%', '0%'],
                scale: [1, 1.2, 0.9, 1],
                transition: {
                    duration: shape.duration / speed,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: shape.delay,
                    type: "tween",
                }
            });
        };
        
        updateAnimation();
        
        if (speedMultiplier && speedMultiplier.on) {
            const unsubscribe = speedMultiplier.on('change', updateAnimation);
            return unsubscribe;
        }
    }, [controls, shape, speedMultiplier]);
    
    return (
        <motion.div
            className={className}
            style={{
                width: `${shape.size}px`,
                height: `${shape.size}px`,
                left: `${shape.x}%`,
                top: `${shape.y}%`,
                background,
                borderRadius,
                opacity: shape.opacity,
                transform: 'translate3d(-50%, -50%, 0)',
                willChange: 'transform, opacity',
                contain: 'layout style paint',
            }}
            animate={controls}
        />
    );
};

const AnimatedBackground = ({ scrollYProgress }) => {
    const { isMobile, prefersReducedMotion, isLowEndDevice } = usePerformance();
    
    // Always call hooks unconditionally
    const defaultScrollProgress = useMotionValue(0);
    const scrollProgressValue = scrollYProgress || defaultScrollProgress;
    
    // Transform scroll progress to speed multiplier (1x to 3x speed)
    const speedMultiplier = useTransform(scrollProgressValue, [0, 1], [1, 3]);

    // Increase number of orbs for more movement - significantly more animated
    const orbCount = useMemo(() => {
        if (prefersReducedMotion) return 0;
        if (isMobile || isLowEndDevice) return 10;
        return 20;
    }, [isMobile, prefersReducedMotion, isLowEndDevice]);

    const accentOrbCount = useMemo(() => {
        if (prefersReducedMotion) return 0;
        if (isMobile || isLowEndDevice) return 8;
        return 15;
    }, [isMobile, prefersReducedMotion, isLowEndDevice]);
    
    // Add floating particles - more particles
    const particleCount = useMemo(() => {
        if (prefersReducedMotion) return 0;
        if (isMobile || isLowEndDevice) return 15;
        return 30;
    }, [isMobile, prefersReducedMotion, isLowEndDevice]);
    
    // Add geometric shapes - more shapes
    const shapeCount = useMemo(() => {
        if (prefersReducedMotion) return 0;
        if (isMobile || isLowEndDevice) return 6;
        return 12;
    }, [isMobile, prefersReducedMotion, isLowEndDevice]);

    // Memoize orb generation to prevent recalculation
    const orbs = useMemo(() => {
        if (orbCount === 0) return [];
        return Array.from({ length: orbCount }, (_, i) => {
            const x1 = Math.random() * 40 - 20; // Increased movement range
            const x2 = Math.random() * 40 - 20;
            const y1 = Math.random() * 40 - 20;
            const y2 = Math.random() * 40 - 20;
            return {
                id: i,
                size: Math.random() * 300 + 150,
                x: Math.random() * 100,
                y: Math.random() * 100,
                opacity: Math.random() * 0.15 + 0.05,
                floatDuration: Math.random() * 12 + 8, // Faster movement
                delay: Math.random() * 5,
                x1, x2, y1, y2,
            };
        });
    }, [orbCount]);

    const accentOrbs = useMemo(() => {
        if (accentOrbCount === 0) return [];
        return Array.from({ length: accentOrbCount }, (_, i) => {
            const x1 = Math.random() * 50 - 25; // Increased movement range
            const x2 = Math.random() * 50 - 25;
            const y1 = Math.random() * 50 - 25;
            const y2 = Math.random() * 50 - 25;
            return {
                id: i,
                size: Math.random() * 100 + 50,
                x: Math.random() * 100,
                y: Math.random() * 100,
                opacity: Math.random() * 0.1 + 0.03,
                floatDuration: Math.random() * 10 + 6, // Faster movement
                delay: Math.random() * 4,
                x1, x2, y1, y2,
            };
        });
    }, [accentOrbCount]);
    
    // Generate floating particles
    const particles = useMemo(() => {
        if (particleCount === 0) return [];
        return Array.from({ length: particleCount }, (_, i) => ({
            id: i,
            size: Math.random() * 8 + 4,
            x: Math.random() * 100,
            y: Math.random() * 100,
            opacity: Math.random() * 0.4 + 0.2,
            duration: Math.random() * 6 + 4, // Faster particles
            delay: Math.random() * 5,
            direction: Math.random() * 360,
            distance: Math.random() * 100 + 50,
        }));
    }, [particleCount]);
    
    // Generate geometric shapes
    const shapes = useMemo(() => {
        if (shapeCount === 0) return [];
        return Array.from({ length: shapeCount }, (_, i) => ({
            id: i,
            size: Math.random() * 60 + 40,
            x: Math.random() * 100,
            y: Math.random() * 100,
            opacity: Math.random() * 0.15 + 0.05,
            duration: Math.random() * 15 + 10, // Faster shapes
            delay: Math.random() * 8,
            rotation: Math.random() * 360,
            type: ['circle', 'square', 'triangle'][Math.floor(Math.random() * 3)],
        }));
    }, [shapeCount]);

    // Reduce blur on mobile for better performance
    const blurAmount = isMobile || isLowEndDevice ? '20px' : '40px';
    const accentBlurAmount = isMobile || isLowEndDevice ? '15px' : '30px';

    if (prefersReducedMotion) {
        return null;
    }

    return (
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
            {/* Main light orbs */}
            {orbs.map((orb) => (
                <ScrollResponsiveOrb
                    key={orb.id}
                    orb={orb}
                    speedMultiplier={speedMultiplier}
                    isMobile={isMobile}
                    blurAmount={blurAmount}
                    className="absolute rounded-full dark:hidden"
                    background="radial-gradient(circle, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.1) 40%, transparent 70%)"
                />
            ))}

            {/* Accent light orbs */}
            {accentOrbs.map((orb) => (
                <ScrollResponsiveOrb
                    key={`accent-${orb.id}`}
                    orb={orb}
                    speedMultiplier={speedMultiplier}
                    isMobile={isMobile}
                    blurAmount={accentBlurAmount}
                    className="absolute rounded-full dark:hidden"
                    background="radial-gradient(circle, rgba(0, 0, 0, 0.25) 0%, rgba(0, 0, 0, 0.08) 50%, transparent 80%)"
                    scaleRange={isMobile ? [1, 1.15, 0.9, 1] : [1, 1.3, 0.8, 1]}
                    opacityRange={[
                        orb.opacity,
                        orb.opacity * (isMobile ? 1.5 : 1.8),
                        orb.opacity * (isMobile ? 0.6 : 0.5),
                        orb.opacity,
                    ]}
                />
            ))}

            {/* Dark mode orbs - inverted for dark theme */}
            {orbs.map((orb) => {
                const darkOrb = { ...orb, opacity: orb.opacity * 1.2 };
                return (
                    <ScrollResponsiveOrb
                        key={`dark-${orb.id}`}
                        orb={darkOrb}
                        speedMultiplier={speedMultiplier}
                        isMobile={isMobile}
                        blurAmount={blurAmount}
                        className="absolute rounded-full dark:block hidden"
                        background="radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.08) 40%, transparent 70%)"
                        scaleRange={[1, 1.2, 0.9, 1]}
                        opacityRange={[
                            darkOrb.opacity,
                            darkOrb.opacity * 1.5,
                            darkOrb.opacity * 0.67,
                            darkOrb.opacity,
                        ]}
                    />
                );
            })}

            {/* Dark mode accent orbs */}
            {accentOrbs.map((orb) => {
                const darkOrb = { ...orb, opacity: orb.opacity * 1.2 };
                return (
                    <ScrollResponsiveOrb
                        key={`dark-accent-${orb.id}`}
                        orb={darkOrb}
                        speedMultiplier={speedMultiplier}
                        isMobile={isMobile}
                        blurAmount={accentBlurAmount}
                        className="absolute rounded-full dark:block hidden"
                        background="radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.06) 50%, transparent 80%)"
                        scaleRange={[1, 1.3, 0.8, 1]}
                        opacityRange={[
                            darkOrb.opacity,
                            darkOrb.opacity * 1.67,
                            darkOrb.opacity * 0.5,
                            darkOrb.opacity,
                        ]}
                    />
                );
            })}
            
            {/* Floating particles - Light mode */}
            {particles.map((particle) => (
                <ScrollResponsiveParticle
                    key={`particle-${particle.id}`}
                    particle={particle}
                    speedMultiplier={speedMultiplier}
                    className="absolute rounded-full dark:hidden"
                    background={`radial-gradient(circle, rgba(139, 92, 246, ${particle.opacity}) 0%, transparent 70%)`}
                />
            ))}
            
            {/* Floating particles - Dark mode */}
            {particles.map((particle) => (
                <ScrollResponsiveParticle
                    key={`dark-particle-${particle.id}`}
                    particle={particle}
                    speedMultiplier={speedMultiplier}
                    className="absolute rounded-full dark:block hidden"
                    background={`radial-gradient(circle, rgba(168, 85, 247, ${particle.opacity}) 0%, transparent 70%)`}
                />
            ))}
            
            {/* Geometric shapes - Light mode */}
            {shapes.map((shape) => (
                <ScrollResponsiveShape
                    key={`shape-${shape.id}`}
                    shape={shape}
                    speedMultiplier={speedMultiplier}
                    className="absolute dark:hidden"
                    background={shape.type === 'circle' 
                        ? 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)'
                        : shape.type === 'square'
                        ? 'linear-gradient(45deg, rgba(236, 72, 153, 0.08) 0%, transparent 50%)'
                        : 'linear-gradient(135deg, rgba(34, 197, 94, 0.08) 0%, transparent 50%)'}
                    borderRadius={shape.type === 'circle' ? '50%' : shape.type === 'square' ? '0%' : '20%'}
                />
            ))}
            
            {/* Geometric shapes - Dark mode */}
            {shapes.map((shape) => (
                <ScrollResponsiveShape
                    key={`dark-shape-${shape.id}`}
                    shape={shape}
                    speedMultiplier={speedMultiplier}
                    className="absolute dark:block hidden"
                    background={shape.type === 'circle' 
                        ? 'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%)'
                        : shape.type === 'square'
                        ? 'linear-gradient(45deg, rgba(168, 85, 247, 0.12) 0%, transparent 50%)'
                        : 'linear-gradient(135deg, rgba(236, 72, 153, 0.12) 0%, transparent 50%)'}
                    borderRadius={shape.type === 'circle' ? '50%' : shape.type === 'square' ? '0%' : '20%'}
                />
            ))}
        </div>
    );
};

export default AnimatedBackground;
